import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Zap, GraduationCap, Users, LogIn, LogOut, Sparkles, Menu, X, Shield } from 'lucide-react';
import { logout as authLogout } from '../lib/auth';
import SAAWordmark from './saa/SAAWordmark';
import { ApplyChoiceModal } from './ApplyChoiceModal';

const GOLD = '#C9A961';
const ORANGE = '#FF5E00';
const BG = '#050508';

const NAV_LINK =
  'text-[11px] font-black tracking-[0.22em] uppercase text-gray-400 hover:text-white transition-colors py-2';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [academyDropdownOpen, setAcademyDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [applyChoiceOpen, setApplyChoiceOpen] = useState(false);
  const openApply = (e) => { e.preventDefault(); setMobileMenuOpen(false); setApplyChoiceOpen(true); };
  const [isStudent, setIsStudent] = useState(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const checkStudentStatus = () => {
      const token = localStorage.getItem('academy_token');
      if (token) {
        setIsAuthed(true);
        try {
          const userData = localStorage.getItem('academy_user');
          if (userData) {
            const user = JSON.parse(userData);
            setIsStudent(user.is_student === true);
            setIsTeacher(user.role === 'teacher' || user.is_admin === true);
          }
        } catch {
          setIsStudent(false);
          setIsTeacher(false);
        }
      } else {
        setIsAuthed(false);
        setIsStudent(false);
        setIsTeacher(false);
      }
    };
    checkStudentStatus();
    window.addEventListener('storage', checkStudentStatus);
    return () => window.removeEventListener('storage', checkStudentStatus);
  }, [location]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setAcademyDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const applyHref = process.env.REACT_APP_ENROLL_URL || '/academy/apply';

  return (
    <header
      className="fixed top-0 w-full z-[100] backdrop-blur-xl transition-all duration-300"
      style={{
        backgroundColor: scrolled ? 'rgba(5,5,8,0.92)' : 'rgba(5,5,8,0.72)',
        borderBottom: `1px solid ${scrolled ? 'rgba(201,169,97,0.18)' : 'rgba(255,255,255,0.05)'}`,
      }}
      data-testid="site-header"
    >
      {/* thin gold hairline */}
      <div className="h-px w-full" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}55, transparent)` }} />

      <nav className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a href="/academy" className="flex items-center gap-3 group flex-shrink-0" data-testid="header-logo">
            <img
              src="/logos/saa-athlete-mark.png"
              alt="Scholar Athlete Academy"
              className="h-9 lg:h-11 w-auto transition-transform group-hover:-translate-y-0.5"
            />
            <SAAWordmark size="xs" stacked className="hidden sm:flex" />

          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {(isStudent || isTeacher) ? (
              <>
                <a
                  href="/dashboard"
                  className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase text-white transition-colors"
                  data-testid="dashboard-nav"
                >
                  <Zap className="h-3.5 w-3.5" style={{ color: ORANGE }} />
                  IQ
                </a>
                <a
                  href="/academy/school"
                  className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase text-white transition-colors"
                  data-testid="my-school-nav"
                >
                  My School
                </a>
                <a
                  href="/solo"
                  className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase transition-colors"
                  style={{ color: ORANGE }}
                  data-testid="solo-nav-student"
                >
                  SOLO K-8
                </a>
                <a
                  href="/apex/course"
                  className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase transition-colors"
                  style={{ color: ORANGE }}
                  data-testid="apex-nav"
                >
                  APEX
                </a>
                <a
                  href="/community"
                  className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase text-white transition-colors"
                  data-testid="community-nav"
                >
                  Community
                </a>
              </>
            ) : (
              <div
                className="relative"
                onMouseEnter={() => setAcademyDropdownOpen(true)}
                onMouseLeave={() => setTimeout(() => {
                  const dd = document.querySelector('[data-dropdown="academy"]');
                  const tr = document.querySelector('[data-trigger="academy"]');
                  if (dd && !dd.matches(':hover') && tr && !tr.matches(':hover')) setAcademyDropdownOpen(false);
                }, 250)}
              >
                <button
                  className={`${NAV_LINK} inline-flex items-center gap-1.5`}
                  data-testid="academy-dropdown-btn"
                  data-trigger="academy"
                >
                  Academy
                  <ChevronDown className="h-3 w-3 opacity-70" />
                </button>

                {academyDropdownOpen && (
                  <>
                    <div className="absolute top-full left-0 w-full h-3" />
                    <div
                      className="absolute top-[calc(100%+8px)] left-0 w-80 py-2 shadow-2xl"
                      data-dropdown="academy"
                      onMouseEnter={() => setAcademyDropdownOpen(true)}
                      onMouseLeave={() => setTimeout(() => setAcademyDropdownOpen(false), 250)}
                      style={{
                        backgroundColor: 'rgba(10,10,14,0.96)',
                        border: `1px solid ${GOLD}30`,
                        backdropFilter: 'blur(20px)',
                      }}
                    >
                      <a href="/solo" className="block px-5 py-3 hover:bg-white/5 transition-colors group border-l-2" style={{ borderColor: ORANGE }} data-testid="solo-main-link">
                        <div className="text-[10px] font-black tracking-[0.28em] uppercase flex items-center gap-2" style={{ color: ORANGE }}>SAA SOLO · K-8 <span className="text-[8px] px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: ORANGE, color: '#000' }}>NEW</span></div>
                        <div className="text-[11px] text-gray-500 mt-1 group-hover:text-gray-300 transition-colors">Autonomous School · $299–$399/mo · Try a Sample Day</div>
                      </a>
                      <a href="/academy" className="block px-5 py-3 hover:bg-white/5 transition-colors group" data-testid="saa-main-link">
                        <div className="text-[10px] font-black tracking-[0.28em] uppercase" style={{ color: ORANGE }}>Scholar Athlete Academy</div>
                        <div className="text-[11px] text-gray-500 mt-1 group-hover:text-gray-300 transition-colors">The Sovereign Path · K-12</div>
                      </a>
                      <a href="/academy?type=parent" className="block px-5 py-3 hover:bg-white/5 transition-colors group" data-testid="spa-main-link">
                        <div className="text-[10px] font-black tracking-[0.28em] uppercase text-purple-400">Scholar Parent Academy</div>
                        <div className="text-[11px] text-gray-500 mt-1 group-hover:text-gray-300 transition-colors">Executive Family Command</div>
                      </a>
                      <a href="/academy/lchaim" className="block px-5 py-3 hover:bg-white/5 transition-colors group" data-testid="lchaim-main-link">
                        <div className="text-[10px] font-black tracking-[0.28em] uppercase text-cyan-400">L&apos;Chaim: The 13th Year</div>
                        <div className="text-[11px] text-gray-500 mt-1 group-hover:text-gray-300 transition-colors">50-Week Faith Companion</div>
                      </a>

                      <div className="mx-5 my-2 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}30, transparent)` }} />

                      <a href="/academy/electives" className="flex items-center gap-3 px-5 py-2.5 text-gray-400 hover:text-white hover:bg-white/5 transition-colors" data-testid="electives-link">
                        <Sparkles className="h-3.5 w-3.5" style={{ color: GOLD }} />
                        <span className="text-[11px] font-bold tracking-[0.15em] uppercase">Elective Courses</span>
                      </a>
                      <a href="/academy/about" className="flex items-center gap-3 px-5 py-2.5 text-gray-400 hover:text-white hover:bg-white/5 transition-colors" data-testid="about-academy-link">
                        <GraduationCap className="h-3.5 w-3.5" style={{ color: GOLD }} />
                        <span className="text-[11px] font-bold tracking-[0.15em] uppercase">About The Academy</span>
                      </a>
                      <a href="/academy/login" className="flex items-center gap-3 px-5 py-2.5 text-gray-400 hover:text-white hover:bg-white/5 transition-colors" data-testid="member-login-link">
                        <LogIn className="h-3.5 w-3.5 text-gray-500" />
                        <span className="text-[11px] font-bold tracking-[0.15em] uppercase">Member Login</span>
                      </a>
                    </div>
                  </>
                )}
              </div>
            )}

            {!isAuthed && (
              <a
                href="/academy/login"
                className={NAV_LINK}
                data-testid="member-login-nav"
              >
                Login
              </a>
            )}

            {isTeacher && (
              <>
                <a
                  href="/teacher-dashboard"
                  className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase text-white transition-colors"
                  data-testid="teacher-dashboard-nav"
                >
                  <Zap className="h-3.5 w-3.5" style={{ color: '#10b981' }} />
                  Flight Control
                </a>
                <a
                  href="/teacher/lesson-plans"
                  className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase transition-colors"
                  style={{ color: '#10b981' }}
                  data-testid="lesson-plans-nav"
                >
                  Lesson Plans
                </a>
              </>
            )}

            {isAuthed && (
              <button
                type="button"
                onClick={() => authLogout('/academy')}
                className={`${NAV_LINK} inline-flex items-center gap-1.5`}
                data-testid="logout-nav"
                aria-label="Log out"
              >
                <LogOut className="h-3 w-3" />
                Logout
              </button>
            )}

            {/* APPLY CTA — sharp-edged orange to match hero "Reserve Your Seat" */}
            <a
              href={applyHref}
              onClick={openApply}
              className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase text-black px-6 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-lg"
              style={{ backgroundColor: ORANGE, boxShadow: `0 0 0 1px ${ORANGE}, 0 8px 20px -8px ${ORANGE}80` }}
              data-testid="apply-nav-tab"
            >
              Apply
              <Shield className="h-3 w-3" />
            </a>
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-3">
            <a
              href={applyHref}
              onClick={openApply}
              className="text-[10px] font-black tracking-[0.22em] uppercase text-black px-4 py-2"
              style={{ backgroundColor: ORANGE }}
              data-testid="apply-nav-tab-mobile-cta"
            >
              Apply
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white p-2"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div
            className="md:hidden pb-6 pt-2"
            style={{ borderTop: `1px solid rgba(255,255,255,0.06)` }}
            data-testid="mobile-menu-panel"
          >
            <div className="flex flex-col gap-1 mt-3">
              {(isStudent || isTeacher) ? (
                <>
                  <a href="/solo" className="mx-3 mt-2 mb-1 px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-black hover:opacity-90 flex items-center justify-between" style={{ backgroundColor: ORANGE }} data-testid="mobile-solo-student">
                    <span>SAA SOLO · K-8</span>
                    <span className="text-[8px]">NEW</span>
                  </a>
                  <a href="/dashboard" className="px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-white hover:bg-white/5" data-testid="mobile-cockpit">IQ</a>
                  <a href="/academy/school" className="px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-white hover:bg-white/5" data-testid="mobile-my-school">My School</a>
                  <a href="/apex/course" className="px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase hover:bg-white/5" style={{ color: ORANGE }} data-testid="mobile-apex">APEX by SAA</a>
                  <a href="/community" className="px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-white hover:bg-white/5" data-testid="mobile-community">Community</a>
                  <a href="/academy/lms" className="px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-gray-400 hover:text-white hover:bg-white/5">Training Ground</a>
                  <a href="/academy/high-performance" className="px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-gray-400 hover:text-white hover:bg-white/5">Performance</a>
                  <a href="/academy/lchaim" className="px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-cyan-400 hover:bg-white/5">L&apos;Chaim</a>
                  {isTeacher && (
                    <>
                      <div className="mx-3 my-2 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}30, transparent)` }} />
                      <a href="/teacher-dashboard" className="px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase hover:bg-white/5" style={{ color: '#10b981' }} data-testid="mobile-flight-control">Flight Control</a>
                      <a href="/teacher/lesson-plans" className="px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase hover:bg-white/5" style={{ color: '#10b981' }} data-testid="mobile-lesson-plans">Lesson Plans</a>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => authLogout('/academy')}
                    className="text-left px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-gray-400 hover:text-white hover:bg-white/5 inline-flex items-center gap-2"
                    data-testid="mobile-logout"
                  >
                    <LogOut className="h-3.5 w-3.5" /> Logout
                  </button>
                </>
              ) : isAuthed ? (
                <>
                  <a href="/academy/school" className="px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-white hover:bg-white/5">My Dashboard</a>
                  <button
                    type="button"
                    onClick={() => authLogout('/academy')}
                    className="text-left px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-gray-400 hover:text-white hover:bg-white/5 inline-flex items-center gap-2"
                    data-testid="mobile-logout"
                  >
                    <LogOut className="h-3.5 w-3.5" /> Logout
                  </button>
                </>
              ) : (
                <>
                  <a href="/solo" className="mx-3 mt-2 mb-1 px-3 py-3 text-[11px] font-black tracking-[0.22em] uppercase text-black hover:opacity-90 flex items-center justify-between" style={{ backgroundColor: ORANGE }} data-testid="mobile-solo-public">
                    <span>SAA SOLO · K-8</span>
                    <span className="text-[8px]">NEW</span>
                  </a>
                  <div className="px-3 pt-3 pb-1 text-[10px] font-black tracking-[0.28em] uppercase" style={{ color: GOLD }}>Academy</div>
                  <a href="/academy" className="px-3 py-2.5 text-[11px] font-bold tracking-[0.18em] uppercase text-white hover:bg-white/5">Scholar Athlete Academy</a>
                  <a href="/academy?type=parent" className="px-3 py-2.5 text-[11px] font-bold tracking-[0.18em] uppercase text-purple-300 hover:bg-white/5">Scholar Parent Academy</a>
                  <a href="/academy/lchaim" className="px-3 py-2.5 text-[11px] font-bold tracking-[0.18em] uppercase text-cyan-300 hover:bg-white/5">L&apos;Chaim: The 13th Year</a>
                  <a href="/academy/electives" className="px-3 py-2.5 text-[11px] font-bold tracking-[0.18em] uppercase text-gray-400 hover:text-white hover:bg-white/5">Elective Courses</a>
                  <a href="/academy/about" className="px-3 py-2.5 text-[11px] font-bold tracking-[0.18em] uppercase text-gray-400 hover:text-white hover:bg-white/5">About The Academy</a>
                  <a href="/academy/login" className="px-3 py-2.5 text-[11px] font-bold tracking-[0.18em] uppercase text-gray-500 hover:text-white hover:bg-white/5">Member Login</a>
                  <a
                    href={applyHref}
                    onClick={openApply}
                    className="mx-3 mt-3 inline-flex items-center justify-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase text-black px-4 py-3"
                    style={{ backgroundColor: ORANGE }}
                    data-testid="apply-nav-tab-mobile"
                  >
                    Apply to the Academy
                    <Shield className="h-3.5 w-3.5" />
                  </a>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
      <ApplyChoiceModal open={applyChoiceOpen} onClose={() => setApplyChoiceOpen(false)} applyHref={applyHref} />
    </header>
  );
};

export default Header;
